//src/features/reporting/report-grid.tsx
import { useRef } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Grid } from "@material-ui/core";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { GridRowId } from "@mui/x-data-grid";
import { ReportGridConfig } from "app-types";
import { useAppState } from "helpers/hooks";
import { getGridColumns, resolveRowValue, hasMeaningfulValue, resolveServerRowCount, isTotalCountKnown, formatUnknownTotalLabel } from "./reporting-helpers";
import LoadingIndicator from "components/loading-indicator";
import GridFooter from "components/grid-footer";
import { useCustomizations, logger } from "@personicom/customizations";
import type { LogCategory } from '@personicom/customizations';
import { FilterCondition, SortCondition } from "./reporting-types";
import { GridFilterItem, GridFilterModel, GridSortModel } from "@mui/x-data-grid";
import ReportToolbar from "./report-toolbar";
import { reportingLogger } from "helpers/reporting-logger";
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { isEqual } from "lodash";
import { DATE_RANGE_END_FILTER_ID, DATE_RANGE_START_FILTER_ID } from "./filters/DateRangeFilter";



interface StyleProps {
  noFrame: boolean;
  isDirty: boolean;
  layoutOffset: number;
}

const buildStyles = makeStyles((theme: any) => ({
  root: {
    padding: `${theme.spacing(0)}px ${theme.spacing(1)}px`, //`,
  },
  content: {
    marginTop: 15,
    [theme.breakpoints.down("xs")]: {
      marginTop: 0,
    }
  },
  gridContainer: (props: StyleProps) => ({
    height: `calc(100vh - ${props.layoutOffset}px)`,
    width: "100%",
    position: "relative",
  }),
  gridWrapper: (props: StyleProps) => ({
    position: "relative",
    height: "100%",
    width: "100%",
    "& .MuiDataGrid-virtualScroller": {
      opacity: props.isDirty ? 0.4 : 1,
      transition: "opacity 0.3s ease"
    }
  }),
  dirtyOverlay: {
    position: "absolute",
    // Position to only cover the virtualScroller (data rows area)
    // Account for toolbar (~56px) and column headers (~56px)
    top: "112px",
    left: 0,
    right: 0,
    // Leave space for footer (~52px)
    bottom: "52px",
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    cursor: "pointer",
    pointerEvents: "auto",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.8)",
    }
  },
  dirtyOverlayContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1),
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    boxShadow: theme.shadows[4],
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 1)",
      boxShadow: theme.shadows[8],
    },
    "&.loading": {
      cursor: "wait",
      opacity: 0.7,
      pointerEvents: "none",
    },
  },
  dirtyOverlayText: {
    marginTop: theme.spacing(1),
    fontSize: "1.2rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    textAlign: "center",
  }
}));

interface ReportGridProps {
  config: ReportGridConfig;
}


const ReportGrid: React.FC<ReportGridProps> = ({ config }) => {
  const { isIframe } = useCustomizations();
  const { sorts, filters, isWorking, items, actions, selectedRow, pagination, page, isDirty, areFiltersInitialized } = useAppState();
  // Tune the layout offset so the grid footer sits near the bottom without triggering a second scroll bar.
  // Recalculate at runtime because some tenants still provide taller legacy headers.
  const calcLayoutOffset = useCallback(() => {
    if (isIframe || typeof document === 'undefined') {
      return isIframe ? 110 : 150;
    }

    const headerHeight = document.getElementById('app-header')?.getBoundingClientRect().height ?? 0;
    return Math.max(150, Math.ceil(headerHeight + 70));
  }, [isIframe]);

  const [layoutOffset, setLayoutOffset] = useState<number>(() => calcLayoutOffset());
  const classes = buildStyles({ noFrame: isIframe, isDirty, layoutOffset });
  const [selection, setSelection] = useState<GridRowId[]>([]);
  var { assetEnvironment, client, subdomain } = useCustomizations();
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const columns = getGridColumns(config, assetEnvironment, client, subdomain);
  const resolvedRowCount = useMemo(
    () => resolveServerRowCount(page.number, page.size, items?.length ?? 0, pagination),
    [items?.length, page.number, page.size, pagination]
  );
  // Wide date ranges (>180 days) suppress the expensive total count, and resolvedRowCount
  // becomes a synthetic keep-paging value. Show "1–100 of 100+" instead of a fake total.
  const totalCountKnown = useMemo(() => isTotalCountKnown(pagination), [pagination]);
  const dateRangeFilterConfig = useMemo(
    () => config.filters?.find((filter) => filter.type === 'dateRange' && !filter.isHidden),
    [config.filters]
  );
  const handleFiltersInitialized = useCallback(() => {
    if (!areFiltersInitialized) {
      actions.setFiltersInitialized(true);
    }
  }, [actions, areFiltersInitialized]);

  useEffect(() => {
    const recalcLayoutOffset = () => setLayoutOffset(calcLayoutOffset());
    recalcLayoutOffset();

    if (isIframe) {
      return;
    }

    window.addEventListener('resize', recalcLayoutOffset);
    const headerImage = document.querySelector<HTMLImageElement>('#app-header img');
    headerImage?.addEventListener('load', recalcLayoutOffset);
    const delayedRecalc = window.setTimeout(recalcLayoutOffset, 250);

    return () => {
      window.removeEventListener('resize', recalcLayoutOffset);
      headerImage?.removeEventListener('load', recalcLayoutOffset);
      window.clearTimeout(delayedRecalc);
    };
  }, [calcLayoutOffset, isIframe]);

  const initializingFilters = useRef(true); // Flag to prevent loop on initial setup
  const isSyncingFilterModel = useRef(false); // Tracks programmatic filter model updates

  /// ========================================
  ///       SORTS
  /// ========================================

  // Sync sortModel with app state sort conditions on initialization and whenever sort state changes from external actions
  useEffect(() => {
    const initializeSortModel = () => {
      const newSortModel: GridSortModel = (sorts ?? []).map((sort) => ({
        field: sort.columnName,
        sort: sort.direction,
      }));
      setSortModel(newSortModel); // Apply the initial sort model only once
    };

    initializeSortModel();
  }, [sorts]); // Run only once on initialization


  const onSortChange = useCallback((newSortModel: GridSortModel) => {
    setSortModel(newSortModel); // Update the local sort model

    const newSortConditions: SortCondition[] = newSortModel.map((sortItem) => ({
      columnName: sortItem.field,
      direction: sortItem.sort,
    }));

    // Update app state with new sort conditions
    actions.setSortAction(newSortConditions);
  }, [actions, sorts]);

  // ========================================
  // Synchronize Filters from App State
  // ========================================
  useEffect(() => {
      const syncFilterModel = () => {
        // Map custom operators to grid-compatible operators for display
        const mapOperatorForGrid = (operator: string): string => {
          switch (operator.toLowerCase()) {
            case 'onorafter': return 'onOrAfter';
            case 'onorbefore': return 'onOrBefore';
            default: return operator;
          }
        };

        // Include all ReportGrid filters for display in grid filter panel
        const reportGridFilters = (filters ?? []).filter(filter => filter.id?.startsWith('ReportGrid-'));
        
        reportingLogger.verbose('ReportGrid: Syncing filters to grid', { 
          totalFilters: filters?.length, 
          reportGridFilters: reportGridFilters.length,
          filterDetails: reportGridFilters 
        }, 'Grid');
        
        const newFilterModel: GridFilterModel = {
          items: reportGridFilters.map((filter, index) => ({
            id: Number(filter.id?.replace('ReportGrid-', '')),
            columnField: filter.columnField,
            operatorValue: mapOperatorForGrid(filter.operatorValue),
            value: filter.value,
          })),
        };
        
        // Only update if the filter model actually changed (prevents infinite loop)
        if (!isEqual(filterModel, newFilterModel)) {
          reportingLogger.verbose('ReportGrid: Setting filterModel', newFilterModel, 'Grid');
          isSyncingFilterModel.current = true;
          setFilterModel(newFilterModel);
          setTimeout(() => {
            isSyncingFilterModel.current = false;
          }, 0);
        }
        initializingFilters.current = false; // Reset after setting initial filters
      };

      syncFilterModel();
  }, [filters, filterModel]);

  // ========================================
  // Handling User Filter Changes
  // ========================================
  const ensureDateRangeCompleteness = useCallback((candidateFilters: FilterCondition[]): FilterCondition[] => {
    if (!dateRangeFilterConfig) {
      return candidateFilters;
    }

    const candidateIds = new Set(candidateFilters.map((filter) => filter.id));
    const shouldIncludeStart = candidateIds.has(DATE_RANGE_START_FILTER_ID);
    const shouldIncludeEnd = candidateIds.has(DATE_RANGE_END_FILTER_ID);

    // Nothing to reconcile when both filters are present or both are absent.
    if ((shouldIncludeStart && shouldIncludeEnd) || (!shouldIncludeStart && !shouldIncludeEnd)) {
      return candidateFilters;
    }

    const preservedFilters = (filters ?? []).filter((filter) => filter.id === DATE_RANGE_START_FILTER_ID || filter.id === DATE_RANGE_END_FILTER_ID);

    if (preservedFilters.length === 0) {
      return candidateFilters;
    }

    reportingLogger.verbose('ReportGrid: Preserving date range filters during grid sync', {
      missingStart: !shouldIncludeStart,
      missingEnd: !shouldIncludeEnd,
      candidateCount: candidateFilters.length,
      preservedCount: preservedFilters.length,
    }, 'Grid');

    const reconciledFilters = [...candidateFilters];

    preservedFilters.forEach((filter) => {
      if (!candidateIds.has(filter.id)) {
        reconciledFilters.push({ ...filter });
      }
    });

    return reconciledFilters;
  }, [dateRangeFilterConfig, filters]);

  const onFilterModelChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      if (!initializingFilters.current) { // Only proceed if not in initialization
        if (isSyncingFilterModel.current) {
          return;
        }

        if (isEqual(filterModel, newFilterModel)) {
          return;
        }

        // Map grid operators back to API operators
        const mapOperatorFromGrid = (operator: string): string => {
          switch (operator) {
            case 'onOrAfter': return 'onOrAfter';
            case 'onOrBefore': return 'onOrBefore';
            default: return operator;
          }
        };

        // Convert new filter model items to filter conditions
        const rawFilterConditions: FilterCondition[] = newFilterModel.items.map((item: GridFilterItem) => ({
          id: `ReportGrid-${item.id}`, // Prefix with ReportGrid- and use item.id
          columnField: item.columnField as string,
          operatorValue: mapOperatorFromGrid(item.operatorValue as string),
          value: item.value ?? null,
        }));

        const newFilterConditions = ensureDateRangeCompleteness(rawFilterConditions);
  
        // Find filters that need to be removed (grid-managed only, never remove date range filters)
        const filtersToRemove = (filters ?? []).filter(
          (existingFilter) => !newFilterConditions.some(
            (newFilter) => newFilter.id === existingFilter.id
          ) && existingFilter.id?.startsWith('ReportGrid-') &&
          existingFilter.id !== DATE_RANGE_START_FILTER_ID &&
          existingFilter.id !== DATE_RANGE_END_FILTER_ID
        );
  
        // Update the app state, first removing obsolete filters, then adding new ones
        if (filtersToRemove.length > 0) {
          actions.removeFiltersAction(filtersToRemove);
        }
        actions.setFiltersAction(newFilterConditions); // Update app state
      }
    },
    [actions, ensureDateRangeCompleteness, filters, filterModel] // Ensure dependencies stay current
  );

  /// ========================================
  ///         Other States
  /// ========================================


  // Get the row ID from the row object
  const getRowId = (row: any): string => {
    const idField = config.idField;
    const resolvedId = resolveRowValue(row, idField);

    if (hasMeaningfulValue(resolvedId)) {
      return String(resolvedId);
    }

    const fallback = resolveRowValue(row, 'renderId') ?? row.renderId;
    logger.warn(`Row ID not found using idField: ${idField}. Falling back to 'renderId'.`, { availableKeys: Object.keys(row ?? {}) }, 'General');

    return String(fallback ?? '');
  };

  // Handle page change
  const onPageChange = useCallback((pageNumber) => {
    pageNumber = pageNumber + 1;  // grid is 0 based.
    actions.changePage(pageNumber);
  }, [actions]);

  // Handle page change
  const onPageSizeChange = useCallback((pageSize) => {
    actions.changePageSize(pageSize);  // Call the new centralized page action
  }, [actions]);

  const onSelectionChange = (newSelection: any) => {
    // newSelection is an array of selected row IDs
    let rowId: string | null;

    if (newSelection && newSelection.length > 0 && newSelection[0] === selectedRow) {
      rowId = null;  // Deselect if the row was already selected
    } else {
      rowId = newSelection[0];  // Select the first row ID from the array
    }

    setSelection(newSelection);
    actions.setSelectedRow(rowId); // Give it to the store
  };

  useEffect(() => {
    const hasVisibleFilters = config.filters?.some((filter) => !filter.isHidden);
    if (!hasVisibleFilters && !areFiltersInitialized) {
      actions.setFiltersInitialized(true);
    }
  }, [actions, areFiltersInitialized, config.filters]);


  if (!items || !config) {
    return <LoadingIndicator message="Initializing grid..." />
  }

  // Show loading indicator if working and not in dirty state (e.g., initial load)
  if (isWorking && !isDirty && items.length === 0) {
    return <LoadingIndicator message="Loading data..." />
  }

  return (
    <Grid id="report-grid-view" container justifyContent="center" className={classes.root}>
      <Grid item xs={12} className={classes.content}>
        <div className={classes.gridContainer}>
          <div className={classes.gridWrapper}>
            {isDirty && (
              <div className={classes.dirtyOverlay}>
                <Box 
                  className={`${classes.dirtyOverlayContent} ${isWorking ? 'loading' : ''}`} 
                  onClick={isWorking ? undefined : actions.refresh}
                >
                  {isWorking ? (
                    <>
                      <CircularProgress size={48} color="secondary" />
                      <Typography className={classes.dirtyOverlayText}>
                        Refreshing...
                      </Typography>
                    </>
                  ) : (
                    <>
                      <RefreshIcon style={{ fontSize: 48 }} color="secondary" />
                      <Typography className={classes.dirtyOverlayText}>
                        Click to Refresh Data
                      </Typography>
                    </>
                  )}
                </Box>
              </div>
            )}
            <DataGridPro
              components={{
                Toolbar: () => (
                  <ReportToolbar
                    onRefresh={actions.refresh}
                    onReset={actions.reset}
                    onExport={actions.exportData}
                    isDirty={isDirty}
                    isWorking={isWorking}
                    dateRangeFilter={dateRangeFilterConfig}
                    filters={filters ?? []}
                    onFiltersChange={actions.setFiltersAction}
                    onFiltersInitialized={handleFiltersInitialized}
                  />
                ),
                Footer: GridFooter,
              }}
              componentsProps={{
                pagination: totalCountKnown ? undefined : { labelDisplayedRows: formatUnknownTotalLabel },
              }}
              loading={isWorking}
              rows={items || []}
              columns={columns}
              page={page.number - 1} //xgrid is 0 based for page number. really page index.
              pageSize={page.size}
              rowsPerPageOptions={[10, 25, 50, 100]}
              density="compact"
              pagination
              getRowId={getRowId}
              rowCount={resolvedRowCount}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              filterMode="server"
              paginationMode="server"
              sortingMode="server"
              sortingOrder={['asc', 'desc']}
              onSortModelChange={onSortChange}
              onFilterModelChange={onFilterModelChange}
              onSelectionModelChange={onSelectionChange}
              sortModel={sortModel}
              selectionModel={selection}
              filterModel={filterModel}
              disableMultipleColumnsSorting={false}
            />
          </div>
        </div>
      </Grid>
    </Grid>
  );
}

export default ReportGrid;
